import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ConversationModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/conversation/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/conversation/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/conversation/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/conversation/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/conversation/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/conversation/CREATE_FAILURE'

const MARK_REQUEST = 'realbase/conversation/MARK_REQUEST'
const MARK_SUCCESS = 'realbase/conversation/MARK_SUCCESS'
const MARK_FAILURE = 'realbase/conversation/MARK_FAILURE'

const SELECT_CONVERSATION = 'realbase/conversation/SELECT_CONVERSATION'

export type ConversationModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  marking: boolean,
}

// Initial State
// @ts-expect-error TS2741
const initialState: ConversationModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function markRequest() {
  return {
    type: MARK_REQUEST,
  }
}

export function markSuccess() {
  return {
    type: MARK_SUCCESS,
  }
}

export function markFailure(errors: Errors = []) {
  return {
    type: MARK_FAILURE,
    errors,
  }
}

export function selectConversation(conversation: ConversationModel) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: SELECT_CONVERSATION,
      selectedId: conversation.id,
    })
  }
}

// Action Creators
export function loadConversations(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().conversations.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/conversations.json', options)
      .then(({ data }: { data: ConversationModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CONVERSATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadConversation(conversationId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.conversations.loadedForKeys]
    const loadedIds = [...state.conversations.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (conversationId && !loadedIds.includes(conversationId)){
      loadedIds.push(conversationId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/conversations/${conversationId}.json`, options)
      .then(({ data }: { data: ConversationModel }) => {
        const normalizedJson = normalize(data, Schemas.CONVERSATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadConversationForSubject(subjectType: string, subjectId: number, options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().conversations.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/conversations/${subjectType}/${subjectId}.json`, options)
      .then(({ data }: { data: Partial<ConversationModel> }) => {
        const normalizedJson = normalize(data, Schemas.CONVERSATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createSubjectConversation(
  conversation: Partial<ConversationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      conversation,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/conversations/create_subject_conversation.json', options, config)
      .then(({ data }: { data: Partial<ConversationModel> }) => {
        const normalizedJson = normalize(data, Schemas.CONVERSATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function markCommentsAsRead(
  conversation: Partial<ConversationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      conversation: { id: conversation.id },
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(markRequest())

    const promise = api(`/conversations/${conversation.id}/mark_comments_as_read.json`, options, config)
      .then(({ data }: { data: ConversationModel }) => {
        const normalizedJson = normalize(data, Schemas.CONVERSATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ConversationModuleState = initialState,
  action: Action = { type: '' },
): ConversationModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case MARK_REQUEST:
      return {
        ...state,
        marking: true,
      }
    case MARK_SUCCESS:
      return {
        ...state,
        marking: false,
        errors: [],
      }
    case MARK_FAILURE:
      return {
        ...state,
        marking: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
